import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { apiFetch } from '../../utils/api';

import {
  FiCheckCircle,
  FiCreditCard,
  FiCheck,
  FiLock,
  FiTruck,
} from "react-icons/fi";
import { loadCart } from "../../feature/cartSlice";

const formatPrice = (n) =>
  `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// Text shown under each coupon code in "View Available Coupons"
const couponLabel = (c) => {
  const off =
    c.discount_type === "percent"
      ? `${Number(c.discount_value)}% off${c.max_discount ? ` (up to ${formatPrice(c.max_discount)})` : ""}`
      : `${formatPrice(c.discount_value)} off`;
  return Number(c.min_order) > 0 ? `${off} on orders above ${formatPrice(c.min_order)}` : off;
};

/* Delivery options: edit fees here (0 shows "Free").
   Keep the ids and fees the same as DELIVERY_FEES in backend/controllers/ordercontroller.js */
const DELIVERY_OPTIONS = [
  { id: "standard", label: "Standard Delivery", eta: "3-5 days", fee: 0 },
  { id: "express", label: "Express Delivery", eta: "1-2 days", fee: 100 },
];

const STEPS = ["Shipping", "Payment", "Review"];

const STATES = [
  "Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan",
  "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

// user saved by Login / Signup, used to pre-fill the form
const savedUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

// Small bordered box with the label on top
function Field({ label, error, className = "", children }) {
  return (
    <div className={className}>
      <label
        className={`block border bg-white px-4 pt-2 pb-2 rounded-md focus-within:border-[#2f6b3a] transition-colors ${
          error ? "border-red-400" : "border-[#d6e0d0]"
        }`}
      >
        <span className="block text-[12px] text-[#6b7a6d]">{label}</span>
        {children}
      </label>
      {error && <p className="mt-1 text-[12px] text-red-500">{error}</p>}
    </div>
  );
}

const inputCls =
  "w-full bg-transparent text-[15px] text-[#1f2d22] focus:outline-none";

function Card({ title, action, children }) {
  return (
    <section className="bg-white border border-[#e3e9dd] rounded-lg p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[17px] sm:text-[18px] font-semibold text-[#1f4a2b]">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Stepper({ step, onGo }) {
  return (
    <ol className="flex items-center gap-2 sm:gap-4 mb-6">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <li key={label} className="flex items-center gap-2 sm:gap-4">
            <button
              type="button"
              disabled={!done}
              onClick={() => onGo(n)}
              className="flex items-center gap-2 disabled:cursor-default"
            >
              <span
                className={`w-[28px] h-[28px] rounded-full flex items-center justify-center text-[13px] font-semibold ${
                  done || active
                    ? "bg-[#2f6b3a] text-white"
                    : "bg-white border border-[#d6e0d0] text-[#9aa79c]"
                }`}
              >
                {done ? <FiCheck /> : n}
              </span>
              <span
                className={`text-[13px] ${
                  active
                    ? "font-semibold text-[#1f4a2b]"
                    : done
                      ? "text-[#2f6b3a]"
                      : "text-[#9aa79c]"
                }`}
              >
                {label}
              </span>
            </button>
            {n < STEPS.length && (
              <span
                className={`w-[28px] sm:w-[64px] h-[2px] ${
                  step > n ? "bg-[#2f6b3a]" : "bg-[#d6e0d0]"
                }`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

const primaryBtn =
  "w-full h-[48px] bg-[#2f6b3a] hover:bg-[#1f4a2b] disabled:opacity-60 transition-colors text-white text-[14px] font-semibold rounded-md";
const secondaryBtn =
  "w-full h-[48px] border border-[#2f6b3a] text-[#2f6b3a] hover:bg-[#eef5ea] transition-colors text-[14px] font-semibold rounded-md";

function Checkout() {
  const items = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    const u = savedUser();
    const [first = "", ...rest] = (u.name || "").split(" ");
    return {
      email: u.email || "",
      firstName: first,
      lastName: rest.join(" "),
      country: "India",
      street: "",
      apartment: "",
      city: "",
      state: "Rajasthan",
      pin: "",
      phone: u.phone || "",
      notes: "",
    };
  });
  const [errors, setErrors] = useState({});
  const [delivery, setDelivery] = useState("standard");
  const [payment, setPayment] = useState("cod");
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState([]); // [{ code, discount }]
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMsg, setCouponMsg] = useState(null); // {type, text}
  const [showCoupons, setShowCoupons] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [serverError, setServerError] = useState("");

  /* ---------- saved addresses ---------- */
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // saved address id, or "new"

  // show the manual form when there are no saved addresses or "different address" is picked
  const usingNew = addresses.length === 0 || selectedId === "new";

  // copy a saved address into the form
  const fillFromAddress = (a) => {
    const [first = "", ...rest] = (a.name || "").split(" ");
    setForm((f) => ({
      ...f,
      firstName: first,
      lastName: rest.join(" "),
      street: a.line || "",
      apartment: "",
      city: a.city || "",
      state: a.state || "Rajasthan",
      pin: a.pincode || "",
      phone: a.phone || "",
    }));
  };

  const selectAddress = (a) => {
    setSelectedId(a.id);
    setErrors({});
    fillFromAddress(a);
  };

  const selectNew = () => {
    setSelectedId("new");
    setErrors({});
    setForm((f) => ({
      ...f,
      firstName: "",
      lastName: "",
      street: "",
      apartment: "",
      city: "",
      state: "Rajasthan",
      pin: "",
      phone: "",
    }));
  };

  // load saved addresses, preselect the default one
  useEffect(() => {
    let cancelled = false;
    apiFetch("/address").then(({ ok, data }) => {
      if (cancelled || !ok || !Array.isArray(data) || data.length === 0) return;
      setAddresses(data);
      const def = data.find((a) => a.is_default) || data[0];
      setSelectedId(def.id);
      fillFromAddress(def);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // load the coupons customers can use
  useEffect(() => {
    apiFetch("/coupons/available").then(({ ok, data }) => {
      if (ok && Array.isArray(data)) setAvailableCoupons(data);
    });
  }, []);

  const deliveryOption = DELIVERY_OPTIONS.find((d) => d.id === delivery);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const discount = Math.min(
    applied.reduce((sum, c) => sum + c.discount, 0),
    subtotal
  );
  const shipping = deliveryOption.fee;
  const total = Math.max(0, subtotal - discount) + shipping;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const goStep = (n) => {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------- coupons (checked by the backend) ---------- */
  const applyCoupon = async (raw) => {
    const code = (raw ?? couponInput).trim().toUpperCase();
    if (!code) return;
    if (applied.some((c) => c.code === code)) {
      setCouponMsg({ type: "error", text: "Coupon already applied." });
      return;
    }
    setCouponBusy(true);
    try {
      const { ok, data } = await apiFetch("/coupons/apply", {
        method: "POST",
        body: { code, subtotal },
      });
      if (!ok) {
        setCouponMsg({ type: "error", text: data?.message || "Invalid coupon code." });
      } else {
        setApplied((a) => [...a, { code: data.code, discount: data.discount }]);
        setCouponMsg({ type: "success", text: "Coupon code applied successfully." });
        setCouponInput("");
      }
    } catch {
      setCouponMsg({ type: "error", text: "Could not apply coupon. Try again." });
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = (code) => {
    setApplied((a) => a.filter((c) => c.code !== code));
    setCouponMsg(null);
  };

  /* ---------- validation ---------- */
  const validate = () => {
    const e = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email address.";
    if (!form.firstName.trim()) e.firstName = "First name is required.";
    // a saved name may be a single word, so only require a last name for a new address
    if (usingNew && !form.lastName.trim()) e.lastName = "Last name is required.";
    if (!form.street.trim()) e.street = "Street address is required.";
    if (!form.city.trim()) e.city = "Town / City is required.";
    if (!/^\d{6}$/.test(form.pin)) e.pin = "Enter a 6-digit PIN code.";
    if (form.phone && !/^\+?\d{10,13}$/.test(form.phone.replace(/\s/g, "")))
      e.phone = "Enter a valid phone number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const continueToPayment = () => {
    if (validate()) goStep(2);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------- place order ---------- */
  const handlePlaceOrder = async (ev) => {
    ev.preventDefault();
    if (step < 3) return;
    setServerError("");
    if (!validate()) {
      goStep(1);
      return;
    }

    try {
      setLoading(true);
      // The server builds the order from your SAVED cart and calculates the
      // real shipping, discount and total itself. We only send the choices.
      const { ok, data } = await apiFetch("/orders", {
        method: "POST",
        body: {
          address: {
            name: `${form.firstName} ${form.lastName}`.trim(),
            phone: form.phone,
            line: [form.street, form.apartment].filter(Boolean).join(", "),
            city: form.city,
            state: form.state,
            pincode: form.pin,
          },
          email: form.email,
          notes: form.notes,
          deliveryMethod: delivery,
          paymentMethod: payment,
          coupons: applied.map((c) => c.code),
        },
      });

      if (!ok) {
        setServerError(data.message || "Something went wrong. Please try again.");
        return;
      }

      setOrderId(data.orderNo);
      dispatch(loadCart()); // the server already emptied the cart, reload it so the site shows 0 items
    } catch (err) {
      setServerError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- success ---------- */
  if (orderId) {
    return (
      <div className="min-h-[80vh] pt-[60px] bg-[#f6f8f2] flex items-center justify-center px-4">
        <div className="text-center py-16 max-w-[460px]">
          <FiCheckCircle className="mx-auto text-[64px] text-[#2f6b3a]" />
          <h2 className="mt-4 font-serif text-[24px] sm:text-[28px] text-[#1f4a2b]">
            Thank you! Your order is placed.
          </h2>
          <p className="mt-2 text-[14px] text-[#6b7a6d]">
            Order ID: <span className="text-[#1f2d22]">{orderId}</span>
          </p>
          <Link
            to="/shop"
            className="inline-block mt-6 h-[44px] leading-[44px] px-8 bg-[#2f6b3a] hover:bg-[#1f4a2b] transition-colors text-white text-[13px] font-semibold rounded-md"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  /* ---------- empty cart ---------- */
  if (items.length === 0) {
    return (
      <div className="min-h-[80vh] pt-[60px] bg-[#f6f8f2] flex items-center justify-center px-4">
        <div className="text-center py-16">
          <h2 className="font-serif text-[22px] sm:text-[26px] text-[#1f4a2b]">
            Your cart is empty
          </h2>
          <Link
            to="/shop"
            className="inline-block mt-6 h-[44px] leading-[44px] px-8 bg-[#2f6b3a] hover:bg-[#1f4a2b] transition-colors text-white text-[13px] font-semibold rounded-md"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const addressLine = [
    form.street,
    form.apartment,
    form.city,
    form.state,
    form.pin,
    form.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="pt-[60px] min-h-screen bg-[#f6f8f2]">
      <form
        onSubmit={handlePlaceOrder}
        noValidate
        className="w-full max-w-[1100px] mx-auto px-4 py-6 sm:py-10"
      >
        <Stepper step={step} onGo={goStep} />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-start">
          {/* ================= LEFT: STEPS ================= */}
          <div className="flex flex-col gap-5 min-w-0">
            {/* ---------- Step 1: Shipping ---------- */}
            {step === 1 && (
              <>
                <Card title="Contact">
                  <Field label="Email Address" error={errors.email}>
                    <input
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      autoComplete="email"
                      className={inputCls}
                    />
                  </Field>
                </Card>

                <Card title="Shipping Address">
                  {/* ---- saved addresses ---- */}
                  {addresses.length > 0 && (
                    <div className="flex flex-col gap-3 mb-4">
                      {addresses.map((a) => (
                        <label
                          key={a.id}
                          className={`flex items-start gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${
                            selectedId === a.id
                              ? "border-[#2f6b3a] bg-[#eef5ea]"
                              : "border-[#d6e0d0] hover:border-[#9fbf9a]"
                          }`}
                        >
                          <input
                            type="radio"
                            name="address"
                            checked={selectedId === a.id}
                            onChange={() => selectAddress(a)}
                            className="accent-[#2f6b3a] mt-1"
                          />
                          <div className="flex-1 text-[14px] text-[#3d4a3f] leading-relaxed">
                            <p className="font-semibold text-[#1f2d22]">
                              {a.name}
                              {a.is_default ? (
                                <span className="ml-2 rounded-full bg-[#dcebd5] px-2 py-0.5 text-[11px] text-[#2f6b3a]">
                                  Default
                                </span>
                              ) : null}
                            </p>
                            <p>
                              {a.line}, {a.city}, {a.state} - {a.pincode}
                            </p>
                            <p className="text-[12px] text-[#6b7a6d]">{a.phone}</p>
                          </div>
                        </label>
                      ))}

                      <label
                        className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${
                          selectedId === "new"
                            ? "border-[#2f6b3a] bg-[#eef5ea]"
                            : "border-[#d6e0d0] hover:border-[#9fbf9a]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="address"
                          checked={selectedId === "new"}
                          onChange={selectNew}
                          className="accent-[#2f6b3a]"
                        />
                        <span className="text-[14px] font-semibold text-[#1f2d22]">
                          Use a different address
                        </span>
                      </label>
                    </div>
                  )}

                  {/* ---- manual address form ---- */}
                  {usingNew && (
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="First name" error={errors.firstName}>
                          <input value={form.firstName} onChange={set("firstName")} autoComplete="given-name" className={inputCls} />
                        </Field>
                        <Field label="Last name" error={errors.lastName}>
                          <input value={form.lastName} onChange={set("lastName")} autoComplete="family-name" className={inputCls} />
                        </Field>
                      </div>

                      <Field label="Country / Region">
                        <select value={form.country} onChange={set("country")} className={`${inputCls} cursor-pointer`}>
                          <option>India</option>
                        </select>
                      </Field>

                      <Field label="Street address" error={errors.street}>
                        <input value={form.street} onChange={set("street")} autoComplete="address-line1" className={inputCls} />
                      </Field>

                      <Field label="Apartment, suite, unit, etc. (optional)">
                        <input value={form.apartment} onChange={set("apartment")} autoComplete="address-line2" className={inputCls} />
                      </Field>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Field label="Town / City" error={errors.city}>
                          <input value={form.city} onChange={set("city")} autoComplete="address-level2" className={inputCls} />
                        </Field>
                        <Field label="State">
                          <select value={form.state} onChange={set("state")} className={`${inputCls} cursor-pointer`}>
                            {STATES.map((s) => (
                              <option key={s}>{s}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="PIN Code" error={errors.pin}>
                          <input
                            value={form.pin}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))
                            }
                            inputMode="numeric"
                            autoComplete="postal-code"
                            className={inputCls}
                          />
                        </Field>
                      </div>

                      <Field label="Phone (optional)" error={errors.phone}>
                        <input type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" className={inputCls} />
                      </Field>
                    </div>
                  )}

                  {/* errors for a saved address (e.g. bad pincode saved earlier) */}
                  {!usingNew && (errors.street || errors.city || errors.pin || errors.phone || errors.firstName) && (
                    <p className="mt-3 text-[13px] text-red-500">
                      This saved address is incomplete. Update it in your Addresses page or choose "Use a different address".
                    </p>
                  )}
                </Card>

                <Card title="Delivery Options">
                  <div className="flex flex-col gap-3">
                    {DELIVERY_OPTIONS.map((d) => (
                      <label
                        key={d.id}
                        className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${
                          delivery === d.id
                            ? "border-[#2f6b3a] bg-[#eef5ea]"
                            : "border-[#d6e0d0] hover:border-[#9fbf9a]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="delivery"
                          checked={delivery === d.id}
                          onChange={() => setDelivery(d.id)}
                          className="accent-[#2f6b3a]"
                        />
                        <FiTruck className="text-[18px] text-[#2f6b3a] shrink-0" />
                        <div className="flex-1">
                          <p className="text-[14px] font-semibold text-[#1f2d22]">{d.label}</p>
                          <p className="text-[12px] text-[#6b7a6d]">{d.eta}</p>
                        </div>
                        <span className="text-[14px] font-semibold text-[#1f2d22]">
                          {d.fee === 0 ? "Free" : formatPrice(d.fee)}
                        </span>
                      </label>
                    ))}
                  </div>
                </Card>

                <Card title="Additional Information">
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={set("notes")}
                    placeholder="Notes about your order, e.g. special notes for delivery."
                    className="w-full border border-[#d6e0d0] rounded-md px-4 py-3 text-[15px] focus:outline-none focus:border-[#2f6b3a] resize-y"
                  />
                </Card>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/cart"
                    className={`${secondaryBtn} flex items-center justify-center sm:w-[180px]`}
                  >
                    ← Back to cart
                  </Link>
                  <button type="button" onClick={continueToPayment} className={primaryBtn}>
                    Continue to Payment
                  </button>
                </div>
              </>
            )}

            {/* ---------- Step 2: Payment ---------- */}
            {step === 2 && (
              <>
                <Card title="Payment Method">
                  <div className="border border-[#d6e0d0] rounded-md divide-y divide-[#e3e9dd] overflow-hidden">
                    <label className="flex items-center gap-3 px-4 py-4 cursor-pointer hover:bg-[#f6f8f2]">
                      <input
                        type="radio"
                        name="payment"
                        checked={payment === "cod"}
                        onChange={() => setPayment("cod")}
                        className="accent-[#2f6b3a]"
                      />
                      <span className="text-[15px]">Cash on Delivery</span>
                    </label>
                    <label className="flex items-center gap-3 px-4 py-4 cursor-pointer hover:bg-[#f6f8f2]">
                      <input
                        type="radio"
                        name="payment"
                        checked={payment === "online"}
                        onChange={() => setPayment("online")}
                        className="accent-[#2f6b3a]"
                      />
                      <FiCreditCard className="text-[18px] text-[#2f6b3a]" />
                      <span className="text-[15px]">Pay online (UPI / Card / Netbanking)</span>
                    </label>
                  </div>
                  {payment === "online" && (
                    <p className="mt-2 text-[12px] text-[#6b7a6d]">
                      Online payment opens after you place the order (connect Razorpay in the next step).
                    </p>
                  )}
                </Card>

                <Card title="Coupons">
                  <button
                    type="button"
                    onClick={() => setShowCoupons((v) => !v)}
                    className="w-full h-[48px] border border-[#d6e0d0] rounded-md text-[14px] text-[#1f4a2b] hover:bg-[#eef5ea] transition-colors"
                  >
                    {showCoupons ? "Hide Available Coupons" : "View Available Coupons"}
                  </button>

                  {showCoupons && (
                    <div className="mt-3 border border-[#d6e0d0] rounded-md divide-y divide-[#e3e9dd]">
                      {availableCoupons.length === 0 && (
                        <p className="px-4 py-3 text-[13px] text-[#6b7a6d]">
                          No coupons available right now.
                        </p>
                      )}
                      {availableCoupons.map((c) => {
                        const isApplied = applied.some((a) => a.code === c.code);
                        return (
                          <div key={c.code} className="flex items-center justify-between gap-3 px-4 py-3">
                            <div>
                              <p className="text-[14px] font-semibold tracking-[1px] text-[#1f2d22]">{c.code}</p>
                              <p className="text-[12px] text-[#6b7a6d]">{couponLabel(c)}</p>
                            </div>
                            <button
                              type="button"
                              disabled={isApplied || couponBusy}
                              onClick={() => applyCoupon(c.code)}
                              className="text-[12px] font-bold uppercase tracking-[1px] text-[#2f6b3a] disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              {isApplied ? "Applied" : "Apply"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="button" onClick={() => goStep(1)} className={`${secondaryBtn} sm:w-[180px]`}>
                    ← Back
                  </button>
                  <button type="button" onClick={() => goStep(3)} className={primaryBtn}>
                    Review Order
                  </button>
                </div>
              </>
            )}

            {/* ---------- Step 3: Review ---------- */}
            {step === 3 && (
              <>
                <Card
                  title="Shipping Details"
                  action={
                    <button type="button" onClick={() => goStep(1)} className="text-[12px] text-[#2f6b3a] hover:underline">
                      Edit
                    </button>
                  }
                >
                  <div className="text-[14px] text-[#3d4a3f] leading-relaxed">
                    <p className="font-semibold text-[#1f2d22]">
                      {`${form.firstName} ${form.lastName}`.trim()}
                    </p>
                    <p>{addressLine}</p>
                    <p className="mt-1 text-[#6b7a6d]">
                      {form.email}
                      {form.phone && ` · ${form.phone}`}
                    </p>
                    {form.notes && (
                      <p className="mt-2 text-[13px] text-[#6b7a6d]">Note: {form.notes}</p>
                    )}
                  </div>
                </Card>

                <Card
                  title="Delivery"
                  action={
                    <button type="button" onClick={() => goStep(1)} className="text-[12px] text-[#2f6b3a] hover:underline">
                      Edit
                    </button>
                  }
                >
                  <p className="text-[14px] text-[#3d4a3f]">
                    {deliveryOption.label} ({deliveryOption.eta}) ·{" "}
                    {deliveryOption.fee === 0 ? "Free" : formatPrice(deliveryOption.fee)}
                  </p>
                </Card>

                <Card
                  title="Payment"
                  action={
                    <button type="button" onClick={() => goStep(2)} className="text-[12px] text-[#2f6b3a] hover:underline">
                      Edit
                    </button>
                  }
                >
                  <p className="text-[14px] text-[#3d4a3f]">
                    {payment === "cod" ? "Cash on Delivery" : "Pay online (UPI / Card / Netbanking)"}
                  </p>
                </Card>

                <p className="text-[13px] text-[#6b7a6d] leading-relaxed">
                  Your personal data will be used to process your order, support your
                  experience throughout this website, and for other purposes described
                  in our{" "}
                  <Link to="/privacy-policy" className="text-[#2f6b3a] hover:underline">
                    privacy policy
                  </Link>
                  .
                </p>

                {serverError && (
                  <p className="text-[14px] text-red-500">{serverError}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="button" onClick={() => goStep(2)} className={`${secondaryBtn} sm:w-[180px]`}>
                    ← Back
                  </button>
                  <button type="submit" disabled={loading} className={primaryBtn}>
                    {loading ? "Placing order..." : `Place Order · ${formatPrice(total)}`}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ================= RIGHT: SUMMARY ================= */}
          <aside className="bg-white border border-[#e3e9dd] rounded-lg p-5 sm:p-6 lg:sticky lg:top-[80px]">
            <h2 className="text-[17px] sm:text-[18px] font-semibold text-[#1f4a2b] mb-5">
              Order Summary
            </h2>

            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <div key={`${item.id}-${item.size ?? ""}`} className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-[56px] h-[56px] object-cover bg-[#f4f7ef] border border-[#e3e9dd] rounded-md"
                    />
                    <span className="absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1 flex items-center justify-center rounded-full bg-[#2f6b3a] text-white text-[11px]">
                      {item.qty}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-[#1f2d22] leading-snug line-clamp-2">{item.name}</p>
                    {item.size && <p className="text-[12px] text-[#6b7a6d]">Size: {item.size}</p>}
                  </div>
                  <p className="text-[13px] font-semibold text-[#1f2d22] shrink-0">
                    {formatPrice(item.price * item.qty)}
                  </p>
                </div>
              ))}
            </div>

            {/* Coupon input */}
            <div className="mt-6 flex gap-2">
              <input
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // don't submit the whole form
                    applyCoupon();
                  }
                }}
                placeholder="Coupon code"
                className="flex-1 min-w-0 h-[42px] border border-[#d6e0d0] bg-white px-3 text-[14px] rounded-md focus:outline-none focus:border-[#2f6b3a]"
              />
              <button
                type="button"
                disabled={couponBusy}
                onClick={() => applyCoupon()}
                className="h-[42px] px-4 bg-[#1f4a2b] hover:bg-[#2f6b3a] disabled:opacity-60 transition-colors text-white text-[12px] font-semibold rounded-md"
              >
                {couponBusy ? "..." : "Apply"}
              </button>
            </div>

            {couponMsg && (
              <p
                className={`mt-3 flex items-center gap-2 text-[13px] ${
                  couponMsg.type === "success" ? "text-[#1f2d22]" : "text-red-500"
                }`}
              >
                {couponMsg.type === "success" && <FiCheckCircle className="text-[#2f6b3a] text-[16px]" />}
                {couponMsg.text}
              </p>
            )}

            {/* Totals */}
            <div className="mt-6 flex flex-col gap-3 text-[14px] text-[#1f2d22]">
              <div className="flex justify-between">
                <span className="text-[#4b574d]">Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>

              {applied.map((c) => (
                <div key={c.code} className="flex justify-between">
                  <span className="text-[#4b574d]">Coupon: {c.code.toLowerCase()}</span>
                  <span>
                    -{formatPrice(c.discount)}{" "}
                    <button
                      type="button"
                      onClick={() => removeCoupon(c.code)}
                      className="text-[#2f6b3a] hover:underline"
                    >
                      [Remove]
                    </button>
                  </span>
                </div>
              ))}

              <div className="flex justify-between">
                <span className="text-[#4b574d]">Shipping</span>
                <span className={shipping === 0 ? "text-[#2f6b3a]" : ""}>
                  {shipping === 0 ? "Free" : formatPrice(shipping)}
                </span>
              </div>

              <div className="flex justify-between items-center pt-4 mt-1 border-t border-[#e3e9dd] text-[19px] font-bold">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-[#e3e9dd] flex items-center justify-around text-[12px] text-[#4b574d]">
              <span className="flex items-center gap-1.5">
                <FiLock className="text-[#2f6b3a]" /> Secure Checkout
              </span>
              <span className="flex items-center gap-1.5">
                <FiCheckCircle className="text-[#2f6b3a]" /> 100% Genuine
              </span>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
}

export default Checkout;
